import { tracksPlanOptions } from '@disco/constants/dropdown-options'
import {
  loadBusinessUsers,
  removeBusinessUser,
} from '@disco/state-management/actions/BusinessUsersActions'
import { updatePricingForm } from '@disco/state-management/actions/FormActions'
import { getPlans } from '@disco/state-management/selectors/pricing'
import {
  getBusinessUsers,
  getNumberOfUsers,
} from '@disco/state-management/selectors/user'
import { ARTIST_ID } from '@disco/utils/featuresAccessLevel'

import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'hooks/use-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import type { Location } from 'react-router-dom'
import Button from 'components/ui/button'
import ButtonContainer from 'components/ui/button-container'
import Flex from 'components/ui/flex'
import Heading from 'components/ui/heading'
import Icon from 'components/ui/icon'
import ModalDialog from 'components/ui/modal-dialog'
import ModalWindow from 'components/ui/modal-window'
import Spinner from 'components/ui/spinner'
import Tag from 'components/ui/tag'
import type { User } from 'types/user'
import styles from './index.module.css'

type ConfirmationDialog = {
  caption: string
  message: string
  confirmButtonCaption: string
  onConfirm: () => void
}

const OverQuotaPlanModal = (): React.ReactNode => {
  const dispatch = useDispatch()
  const location = useLocation()
  const navigate = useNavigate()

  const query = new URLSearchParams(location.search)
  const queryObj = {
    plan: query.get('plan') || '',
    users: query.get('users') || '',
    tracks: query.get('tracks') || '',
  }
  const [confirmationDialog, setConfirmationDialog] =
    useState<ConfirmationDialog | null>(null)
  const plans = useSelector(getPlans)
  const pricingForm = useSelector(state => state.forms.pricingForm)
  const user = useSelector(state => state?.user) || ({} as User)
  const businessUsers = useSelector(getBusinessUsers)
  const number_of_users = useSelector(getNumberOfUsers)
  const isAdmin =
    businessUsers.loaded &&
    businessUsers.users.some(({ user: { id }, type }) => {
      return type === 'administrator' && id === user.id
    })
  const { total_track_count = 0 } = user?.business?.stats || {}
  useEffect(() => {
    if (!businessUsers.loading && !businessUsers.loaded) {
      dispatch(loadBusinessUsers())
    }
  }, [])

  const updateFormToQuota = () => {
    if (!pricingForm.plan) return

    let updatePlan = pricingForm.plan
    const { users, tracks } = pricingForm.componentsQuantity

    if (
      (pricingForm.plan.files.max || 0) < total_track_count ||
      (pricingForm.plan.users.max || 0) < number_of_users
    ) {
      updatePlan = plans.find(({ recommended }) => recommended)
    }

    if (pricingForm.plan.handle === ARTIST_ID) {
      return dispatch(
        updatePricingForm({
          plan: null,
          componentsQuantity: {
            ...pricingForm.componentsQuantity,
            tracks: null,
            users: null,
          },
        }),
      )
    }

    dispatch(
      updatePricingForm({
        plan: updatePlan,
        componentsQuantity: {
          ...pricingForm.componentsQuantity,
          tracks:
            (tracks ?? 0) < total_track_count
              ? tracksPlanOptions.find(value => value >= total_track_count)
              : tracks,
          users: (users ?? 0) < number_of_users ? number_of_users : users,
        },
      }),
    )
  }

  const getLinkPath = () => {
    return location.pathname.includes('/settings/billing/plans')
      ? '/settings/billing/plans'
      : '/settings/billing'
  }

  return (
    <ModalWindow
      header={
        <Heading>
          {businessUsers.loading ? (
            'Loading...'
          ) : (
            <Flex spacing="small">
              <Icon className={styles.overQuotaPlanModalIcon} type="warning" />
              <Flex.Content>Please check your selected plan</Flex.Content>
            </Flex>
          )}
        </Heading>
      }
      actions={
        <ButtonContainer>
          <Button
            caption="Cancel"
            link={getLinkPath()}
            onClick={updateFormToQuota}
          />
          <Button
            appearance="primary"
            caption="Subscribe"
            link={getLinkPath()}
            onClick={updateFormToQuota}
          />
        </ButtonContainer>
      }
      isOpen
      width="medium"
      onClose={() => {
        updateFormToQuota()
        navigate(`${getLinkPath()}`)
      }}
    >
      {businessUsers.loading ? (
        <Spinner />
      ) : (
        <>
          <p className={styles.overQuotaPlanModalDescription}>
            You’ve selected the <b>{queryObj.plan} plan</b> ({queryObj.users}{' '}
            user and {queryObj.tracks} tracks) but you currently have{' '}
            <b>
              <span>
                {number_of_users}{' '}
                <span>{number_of_users > 1 ? 'users' : 'user'}</span>
                <span> and </span>
                {total_track_count}{' '}
                <span>{total_track_count > 1 ? 'tracks' : 'track'}</span>
              </span>
            </b>{' '}
            on your plan. Please sign up to a plan that covers your current
            usage, or remove the additional users and tracks.
            {!isAdmin && (
              <span> Please note only account admins can remove users.</span>
            )}
          </p>
          <div className={styles.overQuotaPlanModalTagsContainer}>
            {businessUsers.users.map(businessUser => {
              const {
                user: { first_name, last_name, id, email },
              } = businessUser
              const isCurrentUser = id === user.id
              let userName = email

              if (first_name || last_name) {
                userName = [first_name, last_name].join(' ')
              }

              return (
                <div className={styles.overQuotaPlanModalTag} key={id}>
                  <Tag
                    onRemoveClick={
                      !isCurrentUser
                        ? () => {
                            setConfirmationDialog({
                              caption: 'Delete user',
                              message: `Remove ${userName} from the team?`,
                              confirmButtonCaption: 'Yes, remove',
                              onConfirm: () =>
                                dispatch(removeBusinessUser(businessUser.id)),
                            })
                          }
                        : undefined
                    }
                    tag={`${userName}${businessUser.removed ? ' (removed)' : ''}
                      ${businessUser.removing ? ' (removing)' : ''}`}
                    icon={<Icon type="profile" width="14" height="14" />}
                    checked={isCurrentUser}
                    disabled={isCurrentUser || !isAdmin}
                  />
                </div>
              )
            })}
          </div>
        </>
      )}

      {confirmationDialog && (
        <ModalDialog
          title={confirmationDialog.caption}
          primaryAction={{
            caption: confirmationDialog.confirmButtonCaption,
            onClick: () => {
              setConfirmationDialog(null)
              confirmationDialog.onConfirm()
            },
          }}
          status="danger"
          onCancel={() => setConfirmationDialog(null)}
        >
          {confirmationDialog.message}
        </ModalDialog>
      )}
    </ModalWindow>
  )
}

export default OverQuotaPlanModal
